/* eslint-disable jsx-a11y/no-interactive-element-to-noninteractive-role --
   The table is laid out with `display: grid` (CMS-1772 R2), and Chrome and
   WebKit drop the native table semantics whenever `display` is overridden. The
   role below looks redundant against the markup but is what keeps the table in
   the accessibility tree, so it must stay. */
import classNames from 'classnames';
import { useCmsClientContext } from '../../../utils/cmsPageContext';
import { getClientTranslations } from '../../../utils/translations/getClientTranslations';
import { CELL_PADDING } from '../constants';
import { TRANSLATIONS } from '../i18n';
import { CalculatedCellValue } from '../types';

interface PersonalLoanCellProps {
  data: Extract<CalculatedCellValue, { provider: 'PL_CALCULATOR' }>;
}

/**
 * Value cell for a calculated personal-loan attribute (CMS-1921 R4/R5).
 *
 * A pure formatter: the value already carries the attribute's own Strapi
 * formatting, and whether a second rate belongs underneath was decided in
 * `resolveCell`. Two treatments only — the highlighted Monthly Repayment, and
 * the stacked interest cell — everything else renders as an ordinary cell.
 */
export const PersonalLoanCell = ({ data }: PersonalLoanCellProps) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);

  return (
    <td
    role="cell"
    className={classNames(
      // No brand tint: PL tables carry a neutral header on every column and no
      // highlighted column (R7).
      'bg-fill/card group-hover/row:bg-fill/base',
      'border-l border-t border-borders/default',
      'min-h-table/cell-min-height',
      CELL_PADDING,
      // Same guard as `ValueCell`: the tracks are fixed at 360/768 and freeze at
      // their minimum on desktop, so a long value cannot widen its way out.
      'break-words overflow-hidden',
    )}
  >
    {/* The two rates are separate lines, not a wrapped one: the gap keeps the
        effective rate readable as its own value (CMS-1921 R4). */}
    <div className="flex h-full flex-col justify-center gap-spacing/gap/soft-8px">
      <span
        className={classNames(
          'text-font-size/table-cell leading-line-height/table-cell',
          // Monthly Repayment is the one highlighted value in the table (R5);
          // the blog table has no sorting, so nothing else is bolded.
          data.highlighted
            ? 'font-font-weight/table-cell-strong text-text/accent'
            : 'font-font-weight/table-cell-normal text-text/primary',
        )}
      >
        {/* "-" for both unresolved states: only the apply form sets `pending`,
            so a table never renders one. A host that does needs a loader here. */}
        {data.result.state === 'resolved' ? data.result.value.formatted : '-'}
      </span>
      {data.effectiveRateText && (
        <span className="font-font-weight/caption text-font-size/caption leading-line-height/caption text-text/secondary-lighter">
          {/* The label is the column's own, not the attribute's: the reader
              needs to know which of the two stacked rates this one is, and only
              this market shows both (R4). */}
          {staticText.eir} {data.effectiveRateText}
        </span>
        )}
      </div>
    </td>
  );
};
